'use client'

import dynamic from 'next/dynamic'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { LandingContent, ProductPhase } from '@/types/landing'
import { track } from '@/lib/analytics'

const PhaseScene = dynamic(() => import('./PhaseScene'), { ssr: false })

function MobilePhaseScene({ phase }: { phase: ProductPhase }) {
  const [approved, setApproved] = useState(false)

  return <div className="fp-mobile-phase-scene" role="group" aria-label={phase.film.label}>
    <div className="fp-mobile-phase-brief" aria-live="polite">
      <span>{phase.number}<small>/04</small></span>
      <p>{phase.description}</p>
    </div>
    {phase.id === 'qualify' && <div className="fp-mobile-phase-artifact fp-mobile-qualify">
      <article className="fp-mobile-mail fp-mobile-mail--incoming">
        <header><span>Incoming RFQ</span><time>9:17 AM</time></header>
        <strong>Nick Douglas · Powell Pump Co.</strong>
        <h3>Impeller MP-4402 · Qty 500</h3>
        <p>Prints attached. Quote in A395 by Friday.</p>
        <small>5 attachments</small>
      </article>
      <span className="fp-mobile-mail-flow" aria-hidden="true">↓</span>
      <article className="fp-mobile-mail fp-mobile-mail--outgoing">
        <header><span>Frank&apos;s reply</span><strong>Ready to send</strong></header>
        <p>Can you send the housing model and confirm A536 vs. A395?</p>
      </article>
    </div>}
    {phase.id === 'quote' && <div className="fp-mobile-phase-artifact fp-mobile-quote">
      <div className="fp-mobile-artifact-heading"><strong>BOM · MP-4402</strong><span>Draft quote</span></div>
      <table>
        <thead><tr><th>Operation</th><th>Qty</th><th>Unit</th></tr></thead>
        <tbody>
          <tr><td>Casting blank</td><td>500</td><td>$62.00</td></tr>
          <tr><td>CNC machining</td><td>500</td><td>$71.00</td></tr>
          <tr><td>Finish + inspect</td><td>500</td><td>$16.20</td></tr>
        </tbody>
      </table>
      <div className="fp-mobile-quote-total"><span>Quoted unit price</span><strong>$197.00</strong></div>
      <button type="button" aria-pressed={approved} onClick={() => setApproved(true)}>
        {approved ? 'Approved ✓' : 'Approve quote'}
      </button>
    </div>}
    {phase.id === 'close' && <div className="fp-mobile-phase-artifact fp-mobile-close">
      <article className="fp-mobile-mail fp-mobile-mail--outgoing">
        <header><span>To Nick Douglas</span><strong>Sent</strong></header>
        <h3>Quote Q-10482 · MP-4402</h3>
        <p>Attached is our quote for 500 impellers. Let me know what questions I can answer.</p>
        <small>Q-10482.pdf · $98,500</small>
      </article>
      <div className="fp-mobile-followup">
        <div><span>Today</span><strong>Quote sent</strong><i aria-hidden="true">✓</i></div>
        <div><span>Day 3</span><strong>Check-in</strong><i aria-hidden="true">•</i></div>
        <div><span>Day 7</span><strong>Final follow-up</strong><i aria-hidden="true">•</i></div>
      </div>
    </div>}
    {phase.id === 'track' && <div className="fp-mobile-phase-artifact fp-mobile-track">
      <div className="fp-mobile-artifact-heading"><strong>MP-4402 · Job activity</strong><span>CRM synced</span></div>
      <ol>
        <li><time>9:17</time><span><strong>RFQ logged</strong><small>5 files attached</small></span></li>
        <li><time>9:19</time><span><strong>Drawing review complete</strong><small>2 questions found</small></span></li>
        <li><time>9:21</time><span><strong>Clarification sent</strong><small>Customer email captured</small></span></li>
        <li><time>11:06</time><span><strong>Requirements confirmed</strong><small>A395 · Qty 500</small></span></li>
        <li><time>11:42</time><span><strong>Quote approved</strong><small>Approved by Mike</small></span></li>
        <li><time>11:44</time><span><strong>Quote sent</strong><small>Day 3 follow-up scheduled</small></span></li>
      </ol>
    </div>}
  </div>
}

export default function ProductPhases({ content }: { content: LandingContent['product'] }) {
  const [active, setActive] = useState(0)
  const [progress, setProgress] = useState(0)
  const [selectionVersion, setSelectionVersion] = useState(0)
  const [shouldLoad, setShouldLoad] = useState(false)
  const [shouldPlay, setShouldPlay] = useState(false)
  const [mobileViewport, setMobileViewport] = useState<boolean | null>(null)
  const sectionRef = useRef<HTMLElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => {
    const query = window.matchMedia('(max-width: 700px)')
    const sync = () => setMobileViewport(query.matches)
    sync()
    query.addEventListener('change', sync)
    return () => query.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    const section = sectionRef.current
    if (!section || !('IntersectionObserver' in window)) {
      setShouldLoad(true)
      setShouldPlay(true)
      return
    }

    const loadObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      setShouldLoad(true)
      loadObserver.disconnect()
    }, { rootMargin: '0px 0px 160px', threshold: 0 })
    const playbackObserver = new IntersectionObserver(([entry]) => {
      setShouldPlay(entry.isIntersecting && entry.intersectionRatio >= .15)
    }, { threshold: [.15] })

    loadObserver.observe(section)
    playbackObserver.observe(section)
    return () => {
      loadObserver.disconnect()
      playbackObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    const tabs = tabsRef.current
    const tab = tabRefs.current[active]
    if (!tabs || !tab || tabs.scrollWidth <= tabs.clientWidth) return

    const left = tab.offsetLeft - (tabs.clientWidth - tab.offsetWidth) / 2
    tabs.scrollTo({
      left: Math.max(0, Math.min(left, tabs.scrollWidth - tabs.clientWidth)),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
  }, [active])

  const select = (index: number) => {
    setActive(index)
    setSelectionVersion((version) => version + 1)
    setProgress(0)
    track('product_phase_selected', {
      phase: content.phases[index].id,
      selection_source: 'manual',
    })
  }

  const handleProgress = useCallback((nextProgress: number) => {
    setProgress(nextProgress)
  }, [])

  const handleEnded = useCallback((endedId: string) => {
    setProgress(0)
    setActive((index) => (content.phases[index].id === endedId ? (index + 1) % content.phases.length : index))
    setSelectionVersion((version) => version + 1)
    track('product_phase_selected', {
      phase: content.phases[(content.phases.findIndex((item) => item.id === endedId) + 1) % content.phases.length].id,
      selection_source: 'automatic',
    })
  }, [content.phases])

  const phase = content.phases[active]

  return (
    <>
      <section className="fp-product-heading"><h2>{content.heading}</h2></section>
      <section id="product" ref={sectionRef} className="fp-product" aria-label="Product phases">
        <div ref={tabsRef} className="fp-phase-tabs" role="tablist" aria-label="Product phases">
          {content.phases.map((item, index) => <button ref={(element) => { tabRefs.current[index] = element }} key={item.id} type="button" role="tab" aria-selected={active === index} aria-controls="fp-product-pane" onClick={() => select(index)}>
            <span className="fp-progress"><span style={{ width: active === index ? `${progress}%` : index < active ? '100%' : '0%' }} /></span>
            <span className="fp-tab-name"><small>{item.number}</small><strong>{item.name}</strong></span><span className="fp-tab-desc">{item.description}</span>
          </button>)}
        </div>
        <div className="fp-product-film" data-product-app>
          <div id="fp-product-pane" role="tabpanel" aria-label={`${phase.name} product scene`}>
            {mobileViewport === true
              ? <MobilePhaseScene key={phase.id} phase={phase} />
              : mobileViewport === false
                ? <PhaseScene
                    key={`${phase.id}-${selectionVersion}`}
                    phase={phase}
                    shouldPlay={shouldLoad && shouldPlay}
                    onProgress={handleProgress}
                    onEnded={handleEnded}
                  />
                : <div className="fp-phase-loading" aria-hidden="true" />}
          </div>
        </div>
      </section>
    </>
  )
}
